/**
 * dashboard/worksheet-kunci.tsx — Pratinjau worksheet vs worksheet + kunci jawaban.
 *
 * Lives at `/dashboard/worksheet-kunci?ws=ws-069`. Approved tutors and admins only.
 * The images are WebP/PNG previews served by the role-gated
 * `/api/tutor/worksheet-preview/:id/:kind`; the downloadable PDF is the worksheet
 * itself (no answer-key PDF is published).
 */

import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { freshSessionQuery, publicCatalogQuery } from '../../lib/api/queries.js';

export interface Search {
  ws?: string;
}

export const Route = createFileRoute('/dashboard/worksheet-kunci')({
  validateSearch: (search: Record<string, unknown>): Search => ({
    ws: typeof search.ws === 'string' && /^ws-\d{3}$/.test(search.ws) ? search.ws : undefined,
  }),
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({ to: '/login', search: { error: undefined, redirect: location.href } });
    }
    if (user.role === 'admin') return null;
    if (user.role === 'parent') throw redirect({ to: '/parent' });
    if (user.role !== 'tutor' || !user.isApprovedTutor) throw redirect({ to: '/me' });
    return null;
  },
  component: WorksheetKunciPage,
});

const previewListQuery = {
  queryKey: ['tutor', 'worksheet-preview'] as const,
  queryFn: async (): Promise<string[]> => {
    const res = await fetch('/api/tutor/worksheet-preview', { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`preview list ${res.status}`);
    const json = (await res.json()) as { data?: { worksheets?: string[] } };
    return json.data?.worksheets ?? [];
  },
  staleTime: 5 * 60_000,
};

function WorksheetKunciPage(): ReactNode {
  const { ws } = Route.useSearch();
  const navigate = useNavigate({ from: '/dashboard/worksheet-kunci' });
  const list = useQuery(previewListQuery);
  const catalog = useQuery(publicCatalogQuery);

  const ids = list.data ?? [];
  const titles = new Map((catalog.data?.worksheets ?? []).map((w) => [w.worksheetId, w]));
  const selected = ws && ids.includes(ws) ? ws : ids[0];
  const selectedMeta = selected ? titles.get(selected) : undefined;
  const pdfHref = selected ? `/api/assets/worksheets/${selected}/v2/pdf` : undefined;

  return (
    <main className="page-shell" data-testid="worksheet-kunci-page">
      <PageHeader
        eyebrow="Panduan mengajar"
        title="Worksheet dan kunci jawaban"
        description="Bandingkan lembar siswa dengan lembar yang sudah berisi kunci jawaban. Pratinjau ini hanya untuk tutor dan admin."
      />

      {list.isLoading ? <p className="form-message">Memuat daftar…</p> : null}
      {list.isError ? (
        <p className="form-message is-error" role="alert">
          Pratinjau tidak dapat dimuat. Coba muat ulang halaman.
        </p>
      ) : null}
      {!list.isLoading && !list.isError && ids.length === 0 ? (
        <p className="form-message">Belum ada kunci jawaban yang tersedia.</p>
      ) : null}

      {ws && !list.isLoading && !ids.includes(ws) ? (
        <p className="form-message" role="status" data-testid="worksheet-kunci-missing">
          Kunci jawaban untuk {ws} belum tersedia. Menampilkan worksheet lain.
        </p>
      ) : null}

      {selected ? (
        <section className="section worksheet-kunci">
          <label className="worksheet-kunci-picker">
            <span>Pilih worksheet</span>
            <select
              value={selected}
              data-testid="worksheet-kunci-select"
              onChange={(e) => void navigate({ search: { ws: e.target.value } })}
            >
              {ids.map((id) => {
                const t = titles.get(id);
                return (
                  <option key={id} value={id}>
                    {id.replace('ws-', '')} — {t?.judul ?? id}
                  </option>
                );
              })}
            </select>
          </label>

          <div className="worksheet-kunci-grid">
            <figure className="card worksheet-kunci-pane">
              <figcaption>Lembar siswa</figcaption>
              <img
                src={`/api/tutor/worksheet-preview/${selected}/soal`}
                alt={`Worksheet ${selectedMeta?.judul ?? selected} tanpa kunci jawaban`}
                data-testid="worksheet-kunci-soal"
              />
            </figure>
            <figure className="card worksheet-kunci-pane">
              <figcaption>Dengan kunci jawaban</figcaption>
              <img
                src={`/api/tutor/worksheet-preview/${selected}/kunci`}
                alt={`Worksheet ${selectedMeta?.judul ?? selected} dengan kunci jawaban berwarna merah`}
                data-testid="worksheet-kunci-kunci"
              />
            </figure>
          </div>

          {pdfHref ? (
            <p>
              <a className="button button-secondary" href={pdfHref} download={`${selected}.pdf`}>
                Unduh PDF worksheet
              </a>
            </p>
          ) : null}
        </section>
      ) : null}
    </main>
  );
}
